import * as React from 'react';
import Comment from './sub/comment';
import Link from './sub/link';
import MainMedia from './sub/main-media';
import {
    NativeAdObj,
    DeviceType,
    Vertical,
    Subvertical,
    PageType,
} from '../../shared/types/ast';
import Logo from './sub/logo';
import Title from './sub/title';
import Badge from './sub/badge';
import Description, { getDescription } from './sub/description';
import TransparencyButton from './sub/transparencyButton';
import useSize from '../hooks/size';

type Props = {
    adObj: NativeAdObj;
    troika?: boolean;
    legacy?: boolean;
    grid?: boolean;
    isBap?: boolean;
    deviceType?: DeviceType;
    pageType?: PageType;
    vertical?: Vertical;
    subvertical?: Subvertical;
};

const Netboard = ({
    adObj,
    troika,
    grid,
    legacy,
    isBap,
    vertical,
    subvertical,
    pageType = 'result',
    deviceType,
}: Props) => {
    if (vertical === 'travel' && subvertical !== 'holiday_rental') {
        if (subvertical === 'prepackage') {
            return (
                <Prepackage
                    adObj={adObj}
                    deviceType={deviceType}
                    pageType={pageType}
                />
            );
        }

        return (
            <Travel adObj={adObj} deviceType={deviceType} pageType={pageType} />
        );
    }

    if (troika) {
        return (
            <TroikaNetboard
                adObj={adObj}
                grid={grid}
                isBap={isBap}
                vertical={vertical}
                deviceType={deviceType}
            />
        );
    } else if (legacy) {
        return <LegacyNetboard adObj={adObj} />;
    }
    return <SpadenNetboard adObj={adObj} />;
};

const SpadenNetboard = ({ adObj, adObj: { native } }: Props) => (
    <div>
        <Comment placement="NewNativeNetboard" adObj={adObj} />
        <div className="native-add native-add-markasadd">
            <div className="native-add__wrap">
                <TransparencyButton
                    adObj={adObj}
                    className="ads-transparency-general ads-transparency-netboard"
                />
                <Link
                    adObj={adObj}
                    className="mod flex align-items-stretch man userhistory licorice unoverflowify"
                >
                    <div className="inner r-pal">
                        <div className="bd">
                            <div className="result-item-image">
                                <div className="format3by2">
                                    <MainMedia
                                        native={native}
                                        className="centered-image"
                                    />
                                </div>
                            </div>
                            <div className="unoverflowify">
                                <div className="d1">
                                    <span
                                        className="mrs mtm bg-banana objectstatus valign-middle"
                                        style={{ whiteSpace: 'normal' }}
                                    >
                                        Annonse fra {native.sponsoredBy}
                                    </span>
                                </div>
                                <Title
                                    h={3}
                                    native={native}
                                    id="native-add__title"
                                    className="t3 word-break mhn mvs result-item-heading"
                                />
                                <Logo
                                    native={native}
                                    placement="new-netboard"
                                    className="result-item-logo"
                                />
                            </div>
                        </div>
                    </div>
                </Link>
            </div>
        </div>
    </div>
);

const LegacyNetboard = ({ adObj, adObj: { native } }: Props) => (
    <div>
        <Comment placement="NativeNetboard" adObj={adObj} />
        <div className="native-add native-add-markasadd">
            <div className="native-add__wrap">
                <TransparencyButton
                    adObj={adObj}
                    className="ads-transparency-general ads-transparency-netboard"
                />
                <Link adObj={adObj} className="native-add__media">
                    <MainMedia
                        native={native}
                        className="native-add__media__img"
                    />
                    <div className="native-add__media__caption">
                        <div className="native-add__sponsoredBy">
                            Annonse fra {native.sponsoredBy}
                        </div>
                        <div className="native-add__title">{native.title}</div>
                        <Logo
                            native={native}
                            placement="netboard"
                            className="result-item-logo"
                        />
                    </div>
                </Link>
            </div>
        </div>
    </div>
);

const TroikaNetboard = ({
    adObj,
    adObj: { native },
    grid,
    deviceType,
}: Props) => {
    const isDescriptionEmpty = !(getDescription(native).length > 0);
    const isMobileList = deviceType === 'mobile' && !grid;

    const MobileListLogoSection = (
        <div className="ads__unit__content__list" style={{ display: 'flex' }}>
            <Description native={native} />
            <Logo
                native={native}
                style={{
                    maxHeight: '48px',
                    width: '48px',
                    minWidth: '40px',
                    marginTop: 'auto',
                    marginBottom: isDescriptionEmpty ? '8px' : undefined,
                    marginLeft: isDescriptionEmpty ? '8px' : 'auto',
                }}
            />
        </div>
    );

    const ListLogoSection = isDescriptionEmpty ? (
        <div style={{ alignSelf: 'flex-end', marginTop: 'auto' }}>
            <Logo
                native={native}
                style={{
                    maxHeight: '48px',
                    maxWidth: '75px',
                }}
            />
        </div>
    ) : (
        <>
            <Description
                native={native}
                className="ads__unit__content__list u-pr8"
            />

            <Logo
                native={native}
                style={{
                    maxHeight: '48px',
                    maxWidth: '75px',
                    alignSelf: 'flex-end',
                    marginTop: 'auto',
                }}
            />
        </>
    );

    const renderListLogoComponent = () =>
        isMobileList ? MobileListLogoSection : ListLogoSection;

    const determineFlexDirection = () => {
        if (grid && isDescriptionEmpty) {
            return 'row';
        }
        if (isMobileList && isDescriptionEmpty) {
            return 'row';
        } else return 'column';
    };

    return (
        <React.Fragment>
            <Comment placement="NewNativeNetboard" adObj={adObj} />
            <div aria-owns={`result-item-heading-${adObj.targetId}`} />
            <div className="ads__unit__img">
                <div className="ads__unit__img__ratio img-format img-format--ratio3by2 img-format--crop-horizontal img-format--centered">
                    <MainMedia native={native} className="img-format__img" />
                </div>
            </div>
            <div className="ads__unit__content">
                <div
                    style={{
                        display: 'flex',
                        flexDirection: determineFlexDirection(),
                        height: grid ? '80%' : '95%',
                    }}
                >
                    <h2
                        className="ads__unit__content__title"
                        id={`result-item-heading-${adObj.targetId}`}
                    >
                        <Link adObj={adObj} className="ads__unit__link">
                            <Title native={native} className="u-pt8" />
                        </Link>
                    </h2>
                    {grid ? (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Description
                                native={native}
                                className="ads__unit__content__list u-pr8"
                            />
                            <Logo
                                native={native}
                                style={{
                                    maxHeight: '48px',
                                    maxWidth: '75px',
                                    marginBottom: isDescriptionEmpty
                                        ? '8px'
                                        : undefined,
                                    marginLeft: 'auto',
                                }}
                            />
                        </div>
                    ) : (
                        renderListLogoComponent()
                    )}
                </div>
                <div className="ads__unit__content__status u-position-relative u-d1">
                    <Badge
                        native={native}
                        style={{ color: 'rgb(118, 118, 118)' }}
                    />
                    <div
                        className="u-position-absolute u-right"
                        style={{ top: '-5px' }}
                    >
                        <TransparencyButton
                            adObj={adObj}
                            style={{
                                filter: '',
                                zIndex: 1,
                                opacity: 0.6,
                            }}
                            placement="right"
                        />
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

const Travel = ({
    adObj,
    adObj: { native },
    deviceType = 'desktop',
}: Props) => {
    const mobile = deviceType === 'mobile';
    return (
        <React.Fragment>
            <Comment placement="Netboard - Travel" adObj={adObj} />
            <Link
                adObj={adObj}
                style={{
                    width: '100%',
                    height: mobile ? '100%' : '170px',
                    display: 'flex',
                    flexDirection: mobile ? 'column' : 'row',
                    textAlign: 'left',
                    color: '#474445',
                    textDecoration: 'none',
                    position: 'relative',
                }}
            >
                <div
                    style={{
                        flex: 3,
                        display: 'flex',
                    }}
                >
                    <MainMedia
                        native={native}
                        style={{
                            borderRadius: mobile ? '8px' : '8px 0 0 8px',
                            objectFit: 'cover',
                            width: '100%',
                            height: '100%',
                        }}
                    />
                </div>
                <div
                    style={{
                        backgroundColor: mobile ? '#ffffff' : '#f6f8fb',
                        flex: 4,
                        borderRadius: '0 8px 8px 0',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div
                        style={{
                            padding: mobile ? '8px 0 0 0' : '16px',
                            position: 'relative',
                            fontSize: '12px !important',
                            width: '100%',
                            height: '100%',
                            flexDirection: 'column',
                        }}
                    >
                        <div
                            className="u-d1"
                            style={{
                                display: 'flex',
                                marginBottom: mobile ? '8px' : '16px',
                                color: '#767676',
                                textAlign: 'center',
                            }}
                        >
                            <div
                                style={{
                                    border: '1px solid rgb(195, 204, 217)',
                                    width: '55px',
                                    marginRight: '8px',
                                    borderRadius: '4px',
                                }}
                            >
                                Annonse
                            </div>
                            <div
                                style={{
                                    color: '#0063fb',
                                    marginTop: '1px',
                                }}
                            >
                                {native.sponsoredBy}
                            </div>
                        </div>
                        <div
                            style={{
                                marginRight: '56px',
                                position: mobile ? 'static' : 'relative',
                                top: mobile ? 0 : '50px',
                            }}
                        >
                            <Title
                                native={native}
                                className="u-b1"
                                style={
                                    mobile
                                        ? {}
                                        : {
                                              position: 'absolute',
                                              bottom: '10px',
                                              marginBottom: 0,
                                          }
                                }
                            />

                            <Description
                                native={native}
                                className="u-d1"
                                style={
                                    mobile
                                        ? {}
                                        : {
                                              position: 'absolute',
                                              top: '-6px',
                                              paddingTop: 0,
                                              marginBottom: 0,
                                          }
                                }
                            />
                        </div>
                        <div
                            style={{
                                height: '48px',
                                width: '48px',
                                position: 'absolute',
                                right: mobile ? 0 : '16px',
                                bottom: mobile ? 0 : '16px',
                            }}
                        >
                            <Logo
                                native={native}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                }}
                            />
                        </div>
                    </div>
                </div>
                <TransparencyButton
                    adObj={adObj}
                    style={{
                        width: '26px',
                        height: '26px',
                        position: 'absolute',
                        right: '0',
                        top: '0',
                        zIndex: 1,
                        borderTopRightRadius: '8px',
                        borderBottomLeftRadius: '13px',
                        backgroundColor: 'rgba(255,255,255,0.45)',
                    }}
                />
            </Link>
        </React.Fragment>
    );
};

const Prepackage = (props: Props) => {
    const {
        adObj,
        adObj: { native },
        deviceType = 'desktop',
        pageType,
    } = props;
    if (pageType === 'object') {
        return <PrepackageObject {...props} />;
    }
    const mobile = deviceType === 'mobile';
    return (
        <React.Fragment>
            <Comment placement="Netboard - Prepackage" adObj={adObj} />
            <Link
                adObj={adObj}
                className="u-mv8"
                style={{
                    width: '100%',
                    height: mobile ? '100%' : '170px',
                    display: 'flex',
                    flexDirection: mobile ? 'column' : 'row',
                    textAlign: 'left',
                    color: '#474445',
                    textDecoration: 'none',
                    position: 'relative',
                }}
            >
                <div
                    style={{
                        flex: 1.74,
                        display: 'flex',
                        paddingLeft: mobile ? '0px' : '16px',
                        paddingRight: mobile ? '0px' : '16px',
                    }}
                >
                    <MainMedia
                        native={native}
                        style={{
                            borderRadius: '8px',
                            border: '1px solid #c3ccd9',
                            objectFit: 'cover',
                            width: '100%',
                            height: '100%',
                        }}
                    />
                </div>
                <div
                    style={{
                        flex: 3,
                        borderRadius: '0 8px 8px 0',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div
                        style={{
                            padding: mobile ? '8px 0 0 0' : '16px',
                            position: 'relative',
                            fontSize: '12px !important',
                            width: '100%',
                            height: '100%',
                            flexDirection: 'column',
                        }}
                    >
                        <div
                            className="u-d1"
                            style={{
                                display: 'flex',
                                marginBottom: mobile ? '8px' : '16px',
                                color: '#767676',
                                textAlign: 'center',
                            }}
                        >
                            <div
                                style={{
                                    border: '1px solid rgb(195, 204, 217)',
                                    width: '55px',
                                    marginRight: '8px',
                                    borderRadius: '4px',
                                }}
                            >
                                Annonse
                            </div>
                            <div
                                style={{
                                    color: '#0063fb',
                                    marginTop: '1px',
                                }}
                            >
                                {native.sponsoredBy}
                            </div>
                        </div>
                        <div
                            style={{
                                marginRight: '56px',
                                position: mobile ? 'static' : 'relative',
                                top: mobile ? 0 : '50px',
                            }}
                        >
                            <Title
                                native={native}
                                className="u-b1"
                                style={
                                    mobile
                                        ? {}
                                        : {
                                              position: 'absolute',
                                              bottom: '10px',
                                              marginBottom: 0,
                                          }
                                }
                            />

                            <Description
                                native={native}
                                className="u-d1"
                                style={
                                    mobile
                                        ? {}
                                        : {
                                              position: 'absolute',
                                              top: '-6px',
                                              paddingTop: 0,
                                              marginBottom: 0,
                                          }
                                }
                            />
                        </div>
                        <div
                            style={{
                                height: '48px',
                                width: '48px',
                                position: 'absolute',
                                right: mobile ? 0 : '16px',
                                bottom: mobile ? 0 : '16px',
                            }}
                        >
                            <Logo
                                native={native}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="u-mt32 u-hide-gt768"></div>
                <TransparencyButton
                    adObj={adObj}
                    style={{
                        width: '26px',
                        height: '26px',
                        position: 'absolute',
                        right: '0',
                        top: '0',
                        zIndex: 1,
                        borderTopRightRadius: '8px',
                        borderBottomLeftRadius: '13px',
                        backgroundColor: 'rgba(255,255,255,0.45)',
                    }}
                />
            </Link>
        </React.Fragment>
    );
};

export function PrepackageObject(props: Props) {
    const {
        adObj,
        adObj: { native },
        deviceType,
    } = props;
    const list = deviceType !== 'mobile';
    const [ref, size] = useSize();
    return (
        <React.Fragment>
            <Comment placement="Netboard - PrepackageObject" adObj={adObj} />
            <Link
                adObj={adObj}
                className="u-mv8"
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: list ? 'row' : 'column',
                    textAlign: 'left',
                    color: '#474445',
                    backgroundColor: '#F6F8FB',
                    textDecoration: 'none',
                    borderRadius: '8px',
                    position: 'relative',
                }}
                ref={ref}
            >
                <div
                    style={{
                        flex: '1.2 1 0%',
                        display: 'flex',
                    }}
                >
                    <MainMedia
                        native={native}
                        style={{
                            borderRadius: list ? '8px 0 0 8px ' : '8px 8px 0 0',
                            objectFit: 'cover',
                            width: '100%',
                            height: '100%',
                        }}
                    />
                </div>
                <div
                    style={{
                        flex: '4 1 0%',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: list
                            ? '8px 16px 16px 16px'
                            : '12px 16px 16px 16px',
                    }}
                >
                    <div
                        className="u-d1"
                        style={{
                            display: 'flex',
                            marginBottom: '8px',
                            color: '#767676',
                        }}
                    >
                        <Badge native={native} />
                    </div>

                    {size === 'small' && (
                        <Title native={native} className="u-t3" />
                    )}

                    <div
                        style={{
                            flex: '1 0 auto',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div
                            style={{
                                flex: '0 1 auto',
                            }}
                        >
                            {size !== 'small' && (
                                <Title native={native} className="u-t3" />
                            )}

                            <Description native={native} className="u-b1" />
                        </div>
                        <Logo
                            native={native}
                            style={{
                                height: 'auto',
                                flex: '0 1 auto',
                                maxHeight: list ? '60px' : '48px',
                                maxWidth: '20%',
                                alignSelf: 'flex-end',
                            }}
                        />
                    </div>
                </div>
                <TransparencyButton
                    adObj={adObj}
                    style={{
                        width: '26px',
                        height: '26px',
                        position: 'absolute',
                        right: '0',
                        top: '0',
                        zIndex: 1,
                        borderTopRightRadius: '8px',
                        borderBottomLeftRadius: '13px',
                        backgroundColor: 'rgba(255,255,255,0.45)',
                    }}
                />
            </Link>
        </React.Fragment>
    );
}
export default Netboard;
